import { useState, type ReactElement } from 'react';

import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useToast } from '@/components/ui/toast';

import { ENTITY_COPY } from './copy';
import { catalogError } from './presentation';
import type { CatalogEntity } from './types';
import { useSaveCatalogItem, type CatalogPath, type CatalogRowsByPath } from './use-catalogs-admin';

export interface ToggleActive<Row> {
  /** Desactivar pide confirmación; reactivar se aplica directo (no es destructivo). */
  request: (row: Row) => void;
  /** El elemento tiene un cambio de estado en curso (su fila queda deshabilitada).
   *  Varios pueden estar en curso a la vez: cada uno se libera al terminar el suyo. */
  isBusy: (id: string) => boolean;
  confirm: ReactElement;
}

/** Desactivar o reactivar un elemento del catálogo con confirmación antes de lo
 *  destructivo y un toast con el resultado real del API (UX-FB-01, §E.5). */
export function useToggleActive<P extends CatalogPath>(
  path: P,
  entity: CatalogEntity,
  labelOf: (row: CatalogRowsByPath[P]) => string,
): ToggleActive<CatalogRowsByPath[P]> {
  const save = useSaveCatalogItem(path);
  const { showToast } = useToast();
  const copy = ENTITY_COPY[entity];
  const [pending, setPending] = useState<CatalogRowsByPath[P] | null>(null);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const isBusy = (id: string): boolean => busy.has(id);

  async function apply(row: CatalogRowsByPath[P], isActive: boolean): Promise<void> {
    const label = labelOf(row);
    setBusy((prev) => new Set(prev).add(row.id));
    try {
      await save.mutateAsync({ id: row.id, body: { isActive } });
      showToast(isActive ? copy.reactivated(label) : copy.deactivated(label));
    } catch (err) {
      showToast(catalogError(err, entity).message, 'error');
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      // Solo cierra la confirmación de ESTE elemento, no la de otro abierta después.
      setPending((p) => (p !== null && p.id === row.id ? null : p));
    }
  }

  const confirm = (
    <ConfirmModal
      open={pending !== null}
      title={copy.deactivateTitle}
      message={pending === null ? '' : copy.deactivateMessage(labelOf(pending))}
      confirmLabel="Desactivar"
      destructive
      busy={pending !== null && isBusy(pending.id)}
      onConfirm={() => {
        if (pending !== null) {
          void apply(pending, false);
        }
      }}
      onCancel={() => setPending(null)}
    />
  );

  return {
    request: (row) => (row.isActive ? setPending(row) : void apply(row, true)),
    isBusy,
    confirm,
  };
}
